/**
 * Partners Slider — Swiper is loaded on demand only when this block is present.
 */

(function () {
    'use strict';

    const SELECTOR = '[data-partners-slider]';
    const SWIPER_SELECTOR = '[data-partners-swiper]';
    let swiperBundlePromise = null;

    function loadSwiperBundle() {
        if (!swiperBundlePromise) {
            swiperBundlePromise = Promise.all([
                import('swiper'),
                import('swiper/modules'),
            ]).then(([swiperMod, modulesMod]) => ({
                Swiper: swiperMod.default || swiperMod.Swiper,
                Autoplay: modulesMod.Autoplay,
            }));
        }

        return swiperBundlePromise;
    }

    function prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /**
     * slidesPerView:auto + loop needs more slides than are typically visible.
     * Duplicate original slides in the DOM so Swiper can loop reliably.
     */
    function padSlidesForLoop(el) {
        const wrapper = el.querySelector('.swiper-wrapper');
        if (!wrapper) {
            return 0;
        }

        const originals = Array.from(wrapper.children).filter((node) =>
            node.classList.contains('swiper-slide')
        );
        const originalCount = originals.length;

        if (originalCount < 2) {
            return originalCount;
        }

        // Enough copies that total slides comfortably exceed a full viewport of logos.
        const targetCount = Math.max(originalCount * 4, 12);

        let i = 0;
        while (wrapper.querySelectorAll(':scope > .swiper-slide').length < targetCount) {
            const source = originals[i % originalCount];
            const clone = source.cloneNode(true);
            clone.removeAttribute('id');
            clone.setAttribute('data-partners-clone', '1');
            wrapper.appendChild(clone);
            i += 1;
        }

        return wrapper.querySelectorAll(':scope > .swiper-slide').length;
    }

    function initSlider(root, { Swiper, Autoplay }) {
        const el = root.querySelector(SWIPER_SELECTOR);
        if (!el || el.swiper) {
            return;
        }

        const originalCount = el.querySelectorAll('.swiper-slide').length;
        if (originalCount < 2) {
            return;
        }

        padSlidesForLoop(el);

        const reduceMotion = prefersReducedMotion();

        const swiper = new Swiper(el, {
            modules: [Autoplay],
            slidesPerView: 'auto',
            centeredSlides: true,
            spaceBetween: 16,
            loop: true,
            loopAdditionalSlides: originalCount,
            loopPreventsSliding: false,
            watchOverflow: false,
            observer: true,
            observeParents: true,
            resizeObserver: true,
            speed: reduceMotion ? 0 : 550,
            grabCursor: true,
            watchSlidesProgress: true,
            slideToClickedSlide: true,
            breakpoints: {
                992: {
                    spaceBetween: 100,
                },
            },
            autoplay: reduceMotion
                ? false
                : {
                      delay: 1800,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                  },
        });

        el.querySelectorAll('img').forEach((img) => {
            if (!img.complete) {
                img.addEventListener('load', () => swiper.update(), { once: true });
            }
        });

        root._partnersSwiper = swiper;
    }

    async function initPartnersSlider(container) {
        const root = container || document;
        const blocks = root.querySelectorAll(SELECTOR);

        if (!blocks.length) {
            return;
        }

        try {
            const bundle = await loadSwiperBundle();
            blocks.forEach((block) => initSlider(block, bundle));
        } catch (error) {
            if (window.sojTheme?.debug) {
                // eslint-disable-next-line no-console
                console.warn('Partners Slider: failed to load Swiper', error);
            }
        }
    }

    function onPageReady(e) {
        const container = e?.detail?.container || document;
        if (container.querySelector(SELECTOR)) {
            initPartnersSlider(container);
        }
    }

    if (document.body?.hasAttribute?.('data-barba')) {
        document.addEventListener('barba:pageReady', onPageReady);
    } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () =>
            onPageReady({ detail: { container: document } })
        );
    } else {
        onPageReady({ detail: { container: document } });
    }
})();
